/** Shared presentational pieces. Deliberately small. */
import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

// ---------------------------------------------------------------------------
// Money and dates
// ---------------------------------------------------------------------------

export function formatMoney(cents: number): string {
  const sign = cents < 0 ? '-' : '';
  const abs = Math.abs(cents);
  return `${sign}$${Math.floor(abs / 100).toLocaleString('en-US')}.${String(abs % 100).padStart(2, '0')}`;
}

export function Money({ cents }: { cents: number | null | undefined }) {
  if (cents === null || cents === undefined) return <span className="muted">—</span>;
  return <span className="num">{formatMoney(cents)}</span>;
}

/** Dollars large, cents small: the cents matter to the record, not to the eye. */
export function Balance({ cents }: { cents: number }) {
  const [dollars, rest] = formatMoney(cents).split('.');
  return (
    <div className="amount">
      {dollars}
      <span className="cents">.{rest}</span>
    </div>
  );
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * "Oct 3" this year, "Oct 3, 2024" otherwise. Lists read better this way; the
 * archive and the audit packet keep ISO dates. Parsed from the string rather
 * than through `Date`, because a calendar day must not shift with the
 * viewer's timezone.
 */
export function formatDate(iso: string | null | undefined, opts: { year?: 'auto' | 'always' } = {}): string {
  if (!iso) return '—';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return iso;
  const [, y, mo, d] = m;
  const month = MONTHS[Number(mo) - 1] ?? mo;
  const sameYear = Number(y) === new Date().getFullYear();
  return sameYear && opts.year !== 'always' ? `${month} ${Number(d)}` : `${month} ${Number(d)}, ${y}`;
}

export function centsToInput(cents: number | null | undefined): string {
  if (cents === null || cents === undefined) return '';
  return (cents / 100).toFixed(2);
}

export function inputToCents(value: string): number | null {
  const cleaned = value.replace(/[^0-9.-]/g, '');
  if (cleaned === '' || cleaned === '-') return null;
  const n = Number(cleaned);
  return Number.isFinite(n) ? Math.round(n * 100) : null;
}

// ---------------------------------------------------------------------------
// Icons. Inline SVG: one stroke style, no icon font, no extra request.
// ---------------------------------------------------------------------------

const PATHS: Record<string, ReactNode> = {
  home: <path d="M4 11l8-7 8 7v9h-5v-6H9v6H4z" />,
  plus: <path d="M12 5v14M5 12h14" />,
  list: <path d="M5 7h14M5 12h14M5 17h14" />,
  receipt: <path d="M7 3h10v18l-2.5-1.7L12 21l-2.5-1.7L7 21zM10 8h4M10 12h4" />,
  cart: <path d="M4 5h2l2 10h9l2-7H7M9.5 19.5h.01M16.5 19.5h.01" />,
  car: <path d="M5 16l1.5-5.5A2 2 0 018.4 9h7.2a2 2 0 011.9 1.5L19 16M4 16h16v3H4zM7.5 19v1.5M16.5 19v1.5" />,
  doc: <path d="M7 3h7l4 4v14H7zM14 3v4h4M10 13h5M10 17h5" />,
  bank: <path d="M4 10l8-5 8 5M6 10v8M10 10v8M14 10v8M18 10v8M4 20h16" />,
  box: <path d="M4 8l8-4 8 4v9l-8 4-8-4zM4 8l8 4 8-4M12 12v9" />,
  people: <path d="M9 11a3 3 0 100-6 3 3 0 000 6zM3.5 19a5.5 5.5 0 0111 0M16 11a2.5 2.5 0 100-5M17 14.2a4.5 4.5 0 013.5 4.8" />,
  help: <path d="M12 21a9 9 0 100-18 9 9 0 000 18zM9.6 9.5a2.5 2.5 0 114 2c-.9.6-1.6 1.2-1.6 2.3M12 17h.01" />,
  out: <path d="M10 5H6v14h4M14 8l4 4-4 4M18 12H9" />,
  back: <path d="M14 6l-6 6 6 6" />,
  chevron: <path d="M9 6l6 6-6 6" />,
  camera: <path d="M4 8h3l1.5-2h7L17 8h3v11H4zM12 16a3 3 0 100-6 3 3 0 000 6z" />,
  upload: <path d="M12 16V5M8 9l4-4 4 4M5 19h14" />,
  search: <path d="M11 18a7 7 0 100-14 7 7 0 000 14zM20 20l-4-4" />,
  flag: <path d="M6 21V4M6 5h11l-2 4 2 4H6" />,
  chart: <path d="M5 20V11M12 20V4M19 20v-6" />,
};

export function Icon({ name }: { name: keyof typeof PATHS | string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.9"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {PATHS[name] ?? PATHS.doc}
    </svg>
  );
}

/** The mark: a receipt with a cross. Same drawing as public/favicon.svg. */
export function Mark() {
  return (
    <svg viewBox="0 0 32 32" aria-hidden="true">
      <rect width="32" height="32" rx="7" fill="var(--accent)" />
      <path d="M10 6h12v20l-2-1.6-2 1.6-2-1.6-2 1.6-2-1.6-2 1.6z" fill="var(--accent-text)" />
      <path d="M15 11h2v3h3v2h-3v3h-2v-3h-3v-2h3z" fill="var(--accent)" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Page furniture
// ---------------------------------------------------------------------------

export function PageHead({
  title,
  children,
  back,
}: {
  title: ReactNode;
  children?: ReactNode;
  back?: { to: string; label: string };
}) {
  return (
    <header className="page-head">
      {back ? (
        <Link className="back" to={back.to}>
          <Icon name="back" />
          {back.label}
        </Link>
      ) : null}
      <h1>{title}</h1>
      {children ? <p>{children}</p> : null}
    </header>
  );
}

/**
 * A notice says what it is in its own title. There is no automatic prefix: a
 * question, a note, and a block read differently, and labelling all three
 * "Blocked:" by color was how a point-in-time note ended up looking like an
 * error. Severity is still never carried by color alone — the title carries it.
 *
 *   stop — cannot proceed until a fact changes
 *   warn — worth a look; the person decides
 *   ok   — done, or all clear
 *   info — context, nothing to do
 */
export function Notice({
  kind = 'info',
  title,
  children,
  actions,
}: {
  kind?: 'stop' | 'warn' | 'ok' | 'info';
  title?: ReactNode;
  children?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className={`notice ${kind === 'info' ? '' : kind}`} role={kind === 'stop' ? 'alert' : 'status'}>
      {title ? <div className="notice-title">{title}</div> : null}
      {children ? <div>{children}</div> : null}
      {actions ? <div className="actions">{actions}</div> : null}
    </div>
  );
}

export function Pill({ tone, children }: { tone?: 'ok' | 'warn' | 'stop'; children: ReactNode }) {
  return <span className={`pill ${tone ?? ''}`}>{children}</span>;
}

export function Empty({ children }: { children: ReactNode }) {
  return <div className="empty">{children}</div>;
}

export function Spinner({ what = 'Loading' }: { what?: string }) {
  return (
    <div className="spinner" role="status">
      {what}…
    </div>
  );
}

/** Placeholder rows while a list loads, so the page does not jump. */
export function Skeleton({ rows = 3 }: { rows?: number }) {
  return (
    <div className="stack tight" role="status" aria-label="Loading">
      {Array.from({ length: rows }, (_, i) => (
        <div className="skeleton" key={i} />
      ))}
    </div>
  );
}

export function ErrorBox({ error }: { error: unknown }) {
  const message = error instanceof Error ? error.message : String(error);
  const requestId =
    typeof error === 'object' && error !== null && 'requestId' in error
      ? String((error as { requestId?: string }).requestId ?? '')
      : '';
  return (
    <Notice kind="stop" title="That did not work">
      {message}
      {requestId ? <div className="mono muted">Request id {requestId}</div> : null}
    </Notice>
  );
}

// ---------------------------------------------------------------------------
// Toasts. One at a time, for "Saved" and the like.
// ---------------------------------------------------------------------------

type Listener = (message: string | null) => void;
let listener: Listener | null = null;
let timer: ReturnType<typeof setTimeout> | undefined;

export function toast(message: string): void {
  if (!listener) return;
  listener(message);
  clearTimeout(timer);
  timer = setTimeout(() => listener?.(null), 2200);
}

export function Toaster() {
  const [message, setMessage] = useState<string | null>(null);
  useEffect(() => {
    listener = setMessage;
    return () => {
      listener = null;
      clearTimeout(timer);
    };
  }, []);
  return (
    <div className="toaster" aria-live="polite">
      {message ? <div className="toast">{message}</div> : null}
    </div>
  );
}
